import React from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

const LINKABLE_TEXT_PATTERN =
    /((?:https?:\/\/|www\.)[^\s<]+|\/[A-Za-z0-9][A-Za-z0-9._~!$&'()*+,;=:@%/-]*(?:\?[^\s<]*)?(?:#[^\s<]*)?)/g;

const TRAILING_PUNCTUATION = new Set([".", ",", "!", "?", ";", ":"]);

const countOccurrences = (value, target) =>
    Array.from(value).reduce(
        (count, character) => count + Number(character === target),
        0,
    );

const splitTrailingPunctuation = (value) => {
    let endIndex = value.length;

    while (endIndex > 0) {
        const character = value[endIndex - 1];

        if (TRAILING_PUNCTUATION.has(character)) {
            endIndex -= 1;
            continue;
        }

        if (character === ")") {
            const candidate = value.slice(0, endIndex);
            const openCount = countOccurrences(candidate, "(");
            const closeCount = countOccurrences(candidate, ")");

            if (closeCount > openCount) {
                endIndex -= 1;
                continue;
            }
        }

        break;
    }

    return {
        core: value.slice(0, endIndex),
        trailing: value.slice(endIndex),
    };
};

const toHref = (value) => {
    if (value.startsWith("/")) return value;
    if (value.startsWith("www.")) return `https://${value}`;
    return value;
};

const isRelativePath = (value) => value.startsWith("/");

const createLinkNode = (value) => ({
    type: "link",
    title: null,
    url: toHref(value),
    children: [
        {
            type: "text",
            value,
        },
    ],
});

const splitTextNode = (value) => {
    const parts = [];
    let cursor = 0;
    let hasMatch = false;

    LINKABLE_TEXT_PATTERN.lastIndex = 0;

    for (const match of value.matchAll(LINKABLE_TEXT_PATTERN)) {
        const matchedValue = match[0];
        const startIndex = match.index ?? 0;
        const { core, trailing } = splitTrailingPunctuation(matchedValue);

        if (!core || core === "/") {
            continue;
        }

        hasMatch = true;

        if (startIndex > cursor) {
            parts.push({
                type: "text",
                value: value.slice(cursor, startIndex),
            });
        }

        parts.push(createLinkNode(core));

        if (trailing) {
            parts.push({
                type: "text",
                value: trailing,
            });
        }

        cursor = startIndex + matchedValue.length;
    }

    if (!hasMatch) return null;

    if (cursor < value.length) {
        parts.push({
            type: "text",
            value: value.slice(cursor),
        });
    }

    return parts;
};

const shouldProcessText = (parentType) =>
    ![
        "link",
        "linkReference",
        "definition",
        "inlineCode",
        "code",
        "html",
    ].includes(parentType);

const transformTextNodes = (node, parentType = null) => {
    if (!node || !Array.isArray(node.children)) return;

    node.children = node.children.flatMap((child) => {
        if (child.type === "text" && shouldProcessText(parentType)) {
            return splitTextNode(child.value) || [child];
        }

        return [child];
    });

    node.children.forEach((child) => transformTextNodes(child, child.type));
};

const remarkLinkifyText = () => (tree) => {
    transformTextNodes(tree);
};

const MARKER_ONLY_PATTERN = /^(\s*(?:[-*+]|\d+\.)\s*)$/;
const LIST_ITEM_PATTERN = /^\s*(?:[-*+]|\d+\.)\s+/;
const INDENTED_CONTENT_PATTERN = /^\s{2,}\S/;

const normalizeMarkdownForChat = (value) => {
    if (!value) return "";

    const lines = String(value).replace(/\r\n?/g, "\n").split("\n");
    const normalized = [];
    let pendingMarker = null;

    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const trimmed = line.trim();
        const nextLine = lines[index + 1] ?? "";
        const nextTrimmed = nextLine.trim();
        const markerMatch = line.match(MARKER_ONLY_PATTERN);

        if (markerMatch) {
            pendingMarker = markerMatch[1];
            continue;
        }

        if (pendingMarker) {
            if (!trimmed) {
                continue;
            }

            normalized.push(`${pendingMarker}${trimmed}`);
            pendingMarker = null;
            continue;
        }

        if (!trimmed) {
            const previousLine = normalized[normalized.length - 1] ?? "";
            const previousIsListContent =
                LIST_ITEM_PATTERN.test(previousLine) ||
                INDENTED_CONTENT_PATTERN.test(previousLine);
            const nextIsListContent =
                LIST_ITEM_PATTERN.test(nextLine) ||
                INDENTED_CONTENT_PATTERN.test(nextLine);

            if (previousIsListContent && nextIsListContent) {
                continue;
            }

            if (!previousLine || !nextTrimmed) {
                continue;
            }
        }

        normalized.push(line);
    }

    if (pendingMarker) {
        normalized.push(pendingMarker.trimEnd());
    }

    return normalized.join("\n").trim();
};

export function MessageMarkdown({ content, isUser, darkMode, inline = false }) {
    const linkClasses = isUser
        ? "tw-font-medium tw-text-white tw-underline tw-decoration-white/60 tw-underline-offset-2 hover:tw-decoration-white"
        : darkMode
          ? "tw-font-medium tw-text-sky-300 tw-underline tw-decoration-sky-400/50 tw-underline-offset-2 hover:tw-text-sky-200"
          : "tw-font-medium tw-text-primary tw-underline tw-decoration-primary/40 tw-underline-offset-2 hover:tw-text-primary/80";
    const mutedBorderClasses = isUser
        ? "tw-border-white/20"
        : darkMode
          ? "tw-border-white/10"
          : "tw-border-slate-200";
    const inlineCodeClasses = isUser
        ? "tw-bg-white/10 tw-text-white"
        : darkMode
          ? "tw-bg-white/10 tw-text-gray-100"
          : "tw-bg-slate-100 tw-text-slate-800";
    const blockCodeClasses = isUser
        ? "tw-bg-white/10 tw-text-white"
        : darkMode
          ? "tw-bg-black/30 tw-text-gray-100"
          : "tw-bg-slate-100 tw-text-slate-800";
    const tableHeaderClasses = isUser
        ? "tw-bg-white/10"
        : darkMode
          ? "tw-bg-white/5"
          : "tw-bg-slate-50";

    const Wrapper = inline ? "span" : "div";
    const wrapperClass = inline
        ? "tw-inline tw-text-[13px] tw-leading-[1.5] tw-break-words"
        : "tw-text-[13px] tw-leading-[1.5] tw-break-words [&_p]:tw-my-0 [&_p+ul]:tw-mt-0.5 [&_p+ol]:tw-mt-0.5 [&_ul]:tw-my-0.5 [&_ol]:tw-my-0.5 [&_li]:tw-my-0 [&_li]:tw-leading-[1.5] [&_li>p]:tw-inline [&_li>p]:tw-whitespace-pre-wrap [&_li>ul]:tw-mt-0.5 [&_li>ol]:tw-mt-0.5";

    return (
        <Wrapper className={wrapperClass}>
            <ReactMarkdown
                skipHtml
                remarkPlugins={[remarkGfm, remarkBreaks, remarkLinkifyText]}
                components={{
                    p: ({ children }) =>
                        inline ? (
                            <span className="tw-whitespace-pre-wrap">{children}</span>
                        ) : (
                            <p className="tw-my-0 tw-text-[13px] tw-whitespace-pre-wrap tw-leading-[1.5]">
                                {children}
                            </p>
                        ),
                    a: ({ href, children }) => {
                        const resolvedHref = href || "#";
                        const isInternalLink = isRelativePath(resolvedHref);

                        return (
                            <a
                                href={resolvedHref}
                                target={isInternalLink ? undefined : "_blank"}
                                rel={
                                    isInternalLink
                                        ? undefined
                                        : "noopener noreferrer"
                                }
                                className={linkClasses}
                            >
                                {children}
                            </a>
                        );
                    },
                    ul: ({ children }) => (
                        <ul className="tw-my-0.5 tw-pl-3 tw-list-disc">
                            {children}
                        </ul>
                    ),
                    ol: ({ children }) => (
                        <ol className="tw-my-0.5 tw-pl-3 tw-list-decimal">
                            {children}
                        </ol>
                    ),
                    li: ({ children }) => (
                        <li className="tw-my-0 tw-whitespace-pre-wrap tw-leading-[1.3]">
                            {children}
                        </li>
                    ),
                    blockquote: ({ children }) => (
                        <blockquote
                            className={`tw-my-2 tw-border-l-2 ${mutedBorderClasses} tw-pl-3 tw-opacity-90`}
                        >
                            {children}
                        </blockquote>
                    ),
                    table: ({ children }) => (
                        <div className="tw-my-2 tw-overflow-x-auto">
                            <table
                                className={`tw-min-w-full tw-border-collapse tw-rounded-lg tw-border ${mutedBorderClasses}`}
                            >
                                {children}
                            </table>
                        </div>
                    ),
                    thead: ({ children }) => (
                        <thead className={tableHeaderClasses}>{children}</thead>
                    ),
                    th: ({ children }) => (
                        <th
                            className={`tw-border ${mutedBorderClasses} tw-px-3 tw-py-2 tw-text-left tw-font-semibold`}
                        >
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td
                            className={`tw-border ${mutedBorderClasses} tw-px-3 tw-py-2 tw-align-top`}
                        >
                            {children}
                        </td>
                    ),
                    img: ({ src, alt }) => (
                        <img
                            src={src}
                            alt={alt || ""}
                            className="tw-max-w-full tw-h-auto tw-rounded-lg tw-my-1"
                            style={{ display: "block" }}
                            loading="lazy"
                        />
                    ),
                    pre: ({ children }) => (
                        <pre
                            className={`tw-my-2 tw-overflow-x-auto tw-rounded-xl tw-p-3 ${blockCodeClasses}`}
                        >
                            {children}
                        </pre>
                    ),
                    code: ({ inline, children }) =>
                        inline ? (
                            <code
                                className={`tw-rounded tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-[0.85em] ${inlineCodeClasses}`}
                            >
                                {children}
                            </code>
                        ) : (
                            <code className="tw-font-mono tw-text-[12px]">
                                {children}
                            </code>
                        ),
                }}
            >
                {normalizeMarkdownForChat(content)}
            </ReactMarkdown>
        </Wrapper>
    );
}
